import React, { useContext, useEffect, useState } from "react";
import FieldMappingModalContext from "../../context/FieldMappingModalContext";
import FieldMappingModalTitle from "../../components/FieldMappingModalTitle";
import FieldMappingModalBackButton from "../../components/FieldMappingModalBackButton";
import AdvancedFieldMappingSelector from "./components/AdvancedFieldMappingSelector";
import { Spinner, TextField } from "@merge-api/merge-javascript-shared";
import CancelAndNextFooter from "../../../../../../../common/CancelAndNextFooter";
import useAdvancedFieldMappingSelection from "./hooks/useAdvancedFieldMappingSelection";
import clsx from "clsx";
import { FieldMappingModalStep } from "../../../../../../../../models/Entities";
import useAppContext from "../../../../../../../context/useAppContext";
import AIGeneratedFieldMappingInput from "./components/AIGeneratedFieldMappingInput";
import { FieldMappingSwitchValue } from "./components/FieldMappingModalSwitch";
import FieldMappingModalSwitch from "./components/FieldMappingModalSwitch";
import { getExpressionLabel } from "../../../utils/expressionUtils";
import { decompressGzipBase64 } from "../../../utils/decompressUtils";

const AdvancedFieldMappingSelection = () => {
  const {
    linkedAccount,
    onModalClose,
    setModalStep,
    selectedRemoteField,
    jsonAdvancedMappingExpression,
    setJsonAdvancedMappingExpression,
  } = useContext(FieldMappingModalContext);

  const [switchValue, setSwitchValue] = useState<string>(
    jsonAdvancedMappingExpression
      ? FieldMappingSwitchValue.Manual
      : FieldMappingSwitchValue.Generated,
  );
  const [hasSubmittedAIQuery, setHasSubmittedAIQuery] = useState<boolean>(false);
  const [isAILoading, setIsAILoading] = useState<boolean>(false);
  // Local state for input to prevent cursor jumping during async evaluation
  const [localInputValue, setLocalInputValue] = useState<string>(
    jsonAdvancedMappingExpression ?? "",
  );
  // Persist the AI prompt across mode switches
  const [aiPromptValue, setAiPromptValue] = useState<string>("");

  const { user } = useAppContext();
  const isJmesPathDeprecationEnabled = user?.is_jmes_path_deprecation_dashboard_enabled ?? false;
  const isFmoPhase3Enabled = user?.is_fmo_loading_optimization_phase_3_enabled ?? false;

  const [jsonData, setJsonData] = useState<any>(selectedRemoteField!.value ?? {});
  const [isDecompressing, setIsDecompressing] = useState<boolean>(false);
  const [decompressError, setDecompressError] = useState<boolean>(false);

  // Decompress the remote field example value if it was compressed on the backend
  useEffect(() => {
    if (
      isFmoPhase3Enabled &&
      selectedRemoteField?.is_compressed_value &&
      selectedRemoteField.compressed_value
    ) {
      setIsDecompressing(true);
      // Clear any prior failure so a re-run for a new field starts clean.
      setDecompressError(false);
      decompressGzipBase64(selectedRemoteField.compressed_value)
        .then((decompressed) => {
          try {
            setJsonData(JSON.parse(decompressed));
          } catch {
            setJsonData(decompressed);
          }
        })
        .catch(() => {
          setDecompressError(true);
        })
        .finally(() => setIsDecompressing(false));
    }
  }, [
    isFmoPhase3Enabled,
    selectedRemoteField?.is_compressed_value,
    selectedRemoteField?.compressed_value,
  ]);

  const {
    inputError,
    errorMessage,
    handleJsonAdvancedMappingExpressionInput,
    jsonAdvancedMappingExpressionResult,
    isJsonAdvancedMapppingExpressionValid,
  } = useAdvancedFieldMappingSelection({
    onJsonAdvancedMappingExpressionChange: setJsonAdvancedMappingExpression,
    jsonData,
  });
  // Sync local input when context value changes externally (e.g., from AI generation)
  useEffect(() => {
    setLocalInputValue(jsonAdvancedMappingExpression ?? "");
  }, [jsonAdvancedMappingExpression]);

  return (
    <>
      <FieldMappingModalTitle
        title={`Map a new ${linkedAccount?.end_user.organization_name}-specific target field`}
        onClose={onModalClose}
      />
      <div className="mx-6 mt-4">
        <div className="flex-col w-full">
          <FieldMappingModalBackButton
            destinationStep={FieldMappingModalStep.REMOTE_FIELD_SELECTION}
          />
          <div className="truncate text-black font-extrabold mt-3.5">{`Advanced mapping - ${
            selectedRemoteField!.display_name
          }`}</div>
          <div className="mt-1">
            {user.is_ai_advanced_field_mapping_generation_enabled &&
            switchValue === FieldMappingSwitchValue.Generated ? (
              <>Describe the data you want to map.</>
            ) : (
              <>
                Select the field inside the list object you want to map, or specify a JSON query
                below using{" "}
                <a
                  href={
                    isJmesPathDeprecationEnabled
                      ? "https://docs.jsonata.org/overview"
                      : "https://jmespath.org/"
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  {getExpressionLabel(isJmesPathDeprecationEnabled)}
                </a>
                .
              </>
            )}
          </div>
          <div className="mt-3">
            {isDecompressing ? (
              <div className="flex items-center justify-center h-[120px]">
                <Spinner size="sm" />
              </div>
            ) : (
              <AdvancedFieldMappingSelector
                jsonString={JSON.stringify(jsonData)}
                fallbackText={
                  decompressError ? String(selectedRemoteField?.value ?? "") : undefined
                }
                handleJsonAdvancedMappingExpressionInput={(jsonAdvancedMappingExpression: any) => {
                  if (
                    !user.is_ai_advanced_field_mapping_generation_enabled ||
                    switchValue == FieldMappingSwitchValue.Manual
                  ) {
                    handleJsonAdvancedMappingExpressionInput(jsonAdvancedMappingExpression);
                  }
                }}
              />
            )}
          </div>
          {user.is_ai_advanced_field_mapping_generation_enabled &&
            switchValue === FieldMappingSwitchValue.Generated && (
              <AIGeneratedFieldMappingInput
                jsonData={jsonData}
                handleJsonAdvancedMappingExpressionInput={handleJsonAdvancedMappingExpressionInput}
                linkedAccount={linkedAccount}
                remoteField={selectedRemoteField?.display_name}
                onEnter={() => {
                  setHasSubmittedAIQuery(true);
                }}
                isLoading={isAILoading}
                setIsLoading={setIsAILoading}
                promptValue={aiPromptValue}
                onPromptChange={setAiPromptValue}
              />
            )}
          {/* Manual mode or non-AI: show the plain TextField */}
          {(!user.is_ai_advanced_field_mapping_generation_enabled ||
            switchValue === FieldMappingSwitchValue.Manual) && (
            <div className="mt-3">
              <TextField
                placeholder="Example query: array[?type=='value'].result"
                multiline
                rows={4}
                inputClassName="resize-y"
                value={localInputValue}
                error={inputError}
                errorText={errorMessage ?? undefined}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setLocalInputValue(newValue);
                  handleJsonAdvancedMappingExpressionInput(newValue);
                }}
              />
            </div>
          )}
          {/* AI Generated mode: show editable expression after loading completes */}
          {user.is_ai_advanced_field_mapping_generation_enabled &&
            switchValue === FieldMappingSwitchValue.Generated &&
            !isAILoading &&
            (jsonAdvancedMappingExpressionResult ||
              (hasSubmittedAIQuery && jsonAdvancedMappingExpression)) && (
              <div className="mt-3 animate-slideDown">
                <TextField
                  placeholder="Example query: array[?type=='value'].result"
                  multiline
                  rows={4}
                  inputClassName="resize-y"
                  value={localInputValue}
                  error={inputError}
                  errorText={errorMessage ?? undefined}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setLocalInputValue(newValue);
                    handleJsonAdvancedMappingExpressionInput(newValue);
                  }}
                />
              </div>
            )}
          {/* Result section */}
          <div
            className={clsx(
              "rounded-md transition-all duration-300 ease-out",
              jsonAdvancedMappingExpressionResult && !isAILoading
                ? "mt-3 px-2 py-2 bg-gray-0 opacity-100"
                : "h-[0px] opacity-0 overflow-hidden",
            )}
          >
            {jsonAdvancedMappingExpressionResult && !isAILoading && (
              <div className="max-h-[120px] overflow-auto">
                <span className="font-semibold mr-1">Result:</span>
                <span className="break-all">{jsonAdvancedMappingExpressionResult}</span>
              </div>
            )}
          </div>

          <div className="mt-6">
            <div
              className={clsx(
                user.is_ai_advanced_field_mapping_generation_enabled &&
                  "flex items-center w-full justify-between",
              )}
            >
              {user.is_ai_advanced_field_mapping_generation_enabled && (
                <FieldMappingModalSwitch
                  switchValue={switchValue}
                  setSwitchValue={setSwitchValue}
                />
              )}
              <CancelAndNextFooter
                isNextDisabled={inputError && !isJsonAdvancedMapppingExpressionValid}
                onCancel={onModalClose}
                onNext={() => {
                  setModalStep(FieldMappingModalStep.REMOTE_FIELD_SELECTION);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdvancedFieldMappingSelection;
